/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel from 'cio/models/campaign';
import { action } from '@ember/object';
import { Periods, PERIOD_KEYS, PERIOD_LABELS, timeRangeToStepsParams } from 'cio/utils/metrics/common';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { Grouping } from 'cio/utils/metrics/resolution';
import { tracked } from '@glimmer/tracking';
import ExportsService from 'cio/services/exports';
import moment from 'moment-timezone';
import { FlyActionDropdownItem } from '@customerio/fly-components/addon/components/fly-action-dropdown';
import SettingsService from 'cio/services/settings';

interface CampaignMetricsPageControlsArgs {
  campaign: CampaignModel;
  timeRange: DateRange;
  timePeriod: Periods;
  onRangeSelect: (range: DateRange, presetValue: string) => void;
  grouping: Grouping;
  onGroupingSelect: (grouping: Grouping) => void;
  unit: 'count' | 'percentage';
  onUnitSelect: (unit: 'count' | 'percentage') => void;
}

enum ExportType {
  AllTime = 'all_time',
  DeliveryMetrics = 'delivery_metrics',
  JourneyMetrics = 'journey_metrics',
  TrackedResponses = 'tracked_responses',
}

export default class CampaignMetricsControlsPage extends Component<CampaignMetricsPageControlsArgs> {
  @service exports!: ExportsService;
  @service settings!: SettingsService;
  @service environment!: EnvironmentService;

  @tracked exportModalState: 'hide' | 'export' | 'exporting' = 'hide';

  periodLabels = PERIOD_LABELS;
  exportPendingImage = new URL('cio/assets/images/export-pending.svg', import.meta.url).toString();
  exportSuccessImage = new URL('cio/assets/images/export-success.svg', import.meta.url).toString();
  exportFailureImage = new URL('cio/assets/images/export-failure.svg', import.meta.url).toString();

  get timeGroupings() {
    let isWeeklyTimePeriod = this.args.timePeriod === Periods.WEEKS;
    let isMonthlyTimePeriod = this.args.timePeriod === Periods.MONTHS;

    let timeGroupings = [
      { label: 'Daily', value: Grouping.Daily, disabled: isWeeklyTimePeriod || isMonthlyTimePeriod },
    ];

    let { timeRange } = this.args;

    let daysInRange = (timeRange.end?.diff(timeRange.start, 'days') ?? 0) + 1;

    if (daysInRange >= 7) {
      timeGroupings.push({ label: 'Weekly', value: Grouping.Weekly, disabled: isMonthlyTimePeriod });
    }

    if (daysInRange >= 62) {
      timeGroupings.push({ label: 'Monthly', value: Grouping.Monthly, disabled: isWeeklyTimePeriod });
    }

    return timeGroupings;
  }

  @action
  onTimePeriodChange() {
    let availableGroupings = this.timeGroupings.filter((g) => !g.disabled).map((g) => g.value);

    if (availableGroupings.includes(PERIOD_KEYS[this.args.timePeriod])) {
      return;
    }

    this.args.onGroupingSelect(PERIOD_KEYS[this.args.timePeriod]);
  }

  exportOptions: FlyActionDropdownItem[] = [
    {
      label: 'All-time metric totals',
      action: () => {
        this.selectedExport = ExportType.AllTime;
        this.exportModalState = 'export';
      },
    },
    {
      label: 'Message metrics',
      action: () => {
        this.selectedExport = ExportType.DeliveryMetrics;
        this.exportModalState = 'export';
      },
    },
    {
      label: 'Journey metrics',
      action: () => {
        this.selectedExport = ExportType.JourneyMetrics;
        this.exportModalState = 'export';
      },
    },
    {
      label: 'Tracked responses',
      action: () => {
        this.selectedExport = ExportType.TrackedResponses;
        this.exportModalState = 'export';
      },
    },
  ];

  @tracked selectedExport: ExportType | null = null;
  journeyMetricsExportResolutionItems = [
    { label: 'By Hour', value: Grouping.Hourly },
    { label: 'By Day', value: Grouping.Daily },
  ];
  @tracked journeyMetricsExportResolution = Grouping.Daily;

  trackedResponsesExportResolutionItems = [
    { label: 'By Hour', value: Grouping.Hourly },
    { label: 'By Day', value: Grouping.Daily },
  ];
  @tracked trackedResponsesExportResolution = Grouping.Daily;

  @action
  hideExportModal() {
    this.exportModalState = 'hide';
    this.selectedExport = null;
    this.journeyMetricsExportResolution = Grouping.Daily;
    this.trackedResponsesExportResolution = Grouping.Daily;
  }

  @action
  createExport() {
    if (this.selectedExport === ExportType.AllTime) {
      this.selectedExport = null;
      return this.exports.deliveryTotals({
        campaignId: this.args.campaign.id,
        from: this.args.campaign.isApiTriggered ? 'triggered_broadcast_all_time' : 'campaign_all_time',
      });
    }

    if (this.selectedExport === ExportType.DeliveryMetrics) {
      this.selectedExport = null;
      let options: Record<string, any> = {
        id: this.args.campaign.id,
        from: this.args.campaign.isApiTriggered
          ? 'triggered_broadcast_volume_and_performance'
          : 'campaign_volume_and_performance',
      };

      let timeRangeParams = timeRangeToStepsParams(this.args.timeRange);

      let timePeriod = this.args.timePeriod === Periods.WEEK ? Periods.CUSTOM : this.args.timePeriod;

      if (timePeriod === Periods.CUSTOM && timeRangeParams?.start != null && timeRangeParams?.steps != null) {
        options.start = timeRangeParams.start;
        options.steps = timeRangeParams.steps;
      }

      options.period = PERIOD_KEYS[this.args.timePeriod];

      return this.exports.deliveryMetrics(options);
    }

    if (this.selectedExport === ExportType.JourneyMetrics) {
      this.selectedExport = null;
      // Sets range start to start of month, because the backend currently doesn't
      // really support start ranges inside months.
      // The backend keeps journey metrics data grouped by month, with the first
      // day of each month as the identifier.
      // This will cause exports to export more data than the user requested.
      // We will follow up later with a backend change, and then this will have to
      // change to "startOf('day')".
      let start = moment
        .tz(
          `${this.args.timeRange.start.format('YYYY-MM-DD')} 00:00:00`,
          this.settings.environmentSettings.journeyMetricsTimezone,
        )
        .startOf('month')
        .unix();
      let end = moment
        .tz(
          `${this.args.timeRange.end.format('YYYY-MM-DD')} 00:00:00`,
          this.settings.environmentSettings.journeyMetricsTimezone,
        )
        .add(1, 'days')
        .startOf('day')
        .unix();

      let resolution = this.journeyMetricsExportResolution;
      this.journeyMetricsExportResolution = Grouping.Daily;

      return this.exports.journeyMetrics({
        id: this.args.campaign.id,
        start,
        end,
        resolution,
      });
    }

    if (this.selectedExport === ExportType.TrackedResponses) {
      this.selectedExport = null;
      let start = moment
        .tz(
          `${this.args.timeRange.start.format('YYYY-MM-DD')} 00:00:00`,
          this.settings.environmentSettings.metricsTimezone,
        )
        .startOf('day')
        .unix();
      let end = moment
        .tz(
          `${this.args.timeRange.end.format('YYYY-MM-DD')} 00:00:00`,
          this.settings.environmentSettings.metricsTimezone,
        )
        .add(1, 'days')
        .startOf('day')
        .unix();

      let resolution = this.trackedResponsesExportResolution;
      this.trackedResponsesExportResolution = Grouping.Daily;

      return this.exports.trackedResponses({
        id: this.args.campaign.id,
        start,
        end,
        resolution,
      });
    }

    this.selectedExport = null;
    return;
  }
}
